<template>
  <AdminLayout page-title="Support Inbox" page-subtitle="Read and reply to platform users">
    <div class="space-y-6">
      <div class="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-white mb-2">Support Inbox</h1>
          <p class="text-slate-400">Conversations from contractors, companies, employees, and other platform users.</p>
        </div>
        <button class="btn-oilbuddy-primary" @click="openCompose">Message a user</button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="admin-stat-card">
          <p class="text-sm text-slate-400">Open</p>
          <p class="text-3xl font-bold text-white">{{ stats.open }}</p>
        </div>
        <div class="admin-stat-card">
          <p class="text-sm text-slate-400">In progress</p>
          <p class="text-3xl font-bold text-white">{{ stats.inProgress }}</p>
        </div>
        <div class="admin-stat-card">
          <p class="text-sm text-slate-400">Closed</p>
          <p class="text-3xl font-bold text-white">{{ stats.closed }}</p>
        </div>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-12 gap-6 min-h-[32rem]">
        <aside class="xl:col-span-4 admin-stat-card p-0 overflow-hidden flex flex-col">
          <div class="p-4 border-b border-slate-700/50 space-y-3">
            <input v-model="search" type="search" class="form-input" placeholder="Search name, email, or subject">
            <div class="flex flex-wrap gap-2">
              <button
                v-for="filter in statusFilters"
                :key="filter.id"
                class="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors"
                :class="statusFilter === filter.id
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                  : 'text-slate-400 border-slate-700 hover:text-white'"
                @click="statusFilter = filter.id"
              >
                {{ filter.label }}
              </button>
            </div>
          </div>
          <div class="flex-1 overflow-y-auto">
            <p v-if="isLoading" class="p-6 text-slate-400">Loading conversations…</p>
            <p v-else-if="!filteredTickets.length" class="p-6 text-slate-400">No conversations yet. Users reach you from their Support page, or message one from here.</p>
            <button
              v-for="ticket in filteredTickets"
              :key="ticket.id"
              class="w-full text-left px-4 py-4 border-b border-slate-800/80 hover:bg-slate-700/30 transition-colors"
              :class="selectedId === ticket.id ? 'bg-emerald-500/10' : ''"
              @click="selectTicket(ticket.id)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-white font-semibold truncate">{{ ticket.userName || ticket.userEmail || 'Platform user' }}</p>
                  <p class="text-sm text-slate-400 truncate">{{ ticket.subject }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ accountLabel(ticket) }} · {{ ticket.updatedAt || ticket.createdAt }}</p>
                </div>
                <span class="shrink-0 px-2 py-0.5 rounded-full text-xs font-medium" :class="statusClass(ticket.status)">
                  {{ statusLabel(ticket.status) }}
                </span>
              </div>
            </button>
          </div>
        </aside>

        <section class="xl:col-span-8 admin-stat-card flex flex-col min-h-[32rem]">
          <div v-if="!selected" class="flex-1 grid place-items-center text-slate-400">
            Select a conversation to reply.
          </div>
          <template v-else>
            <div class="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-slate-700/50">
              <div>
                <h2 class="text-xl font-semibold text-white">{{ selected.subject }}</h2>
                <p class="text-sm text-slate-400 mt-1">
                  {{ selected.userName || 'User' }}
                  <span v-if="selected.userEmail"> · {{ selected.userEmail }}</span>
                  · {{ accountLabel(selected) }}
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <button class="btn-secondary text-sm" @click="setStatus('open')">Open</button>
                <button class="btn-secondary text-sm" @click="setStatus('in_progress')">In progress</button>
                <button class="btn-secondary text-sm" @click="setStatus('closed')">Close</button>
              </div>
            </div>

            <div class="flex-1 overflow-y-auto py-4 space-y-4">
              <article class="rounded-2xl border border-slate-700/50 bg-slate-800/40 p-4">
                <p class="text-xs uppercase tracking-wide text-slate-500 mb-2">{{ selected.userName || 'User' }} · {{ selected.createdAt }}</p>
                <p class="text-slate-200 whitespace-pre-wrap">{{ selected.message }}</p>
              </article>
              <article
                v-for="reply in replies"
                :key="reply.id"
                class="rounded-2xl border p-4"
                :class="reply.senderType === 'admin'
                  ? 'border-emerald-500/20 bg-emerald-500/10 ml-8'
                  : 'border-slate-700/50 bg-slate-800/40 mr-8'"
              >
                <p class="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  {{ reply.senderType === 'admin' ? 'Admin' : (reply.senderName || selected.userName || 'User') }} · {{ reply.createdAt }}
                </p>
                <p class="text-slate-200 whitespace-pre-wrap">{{ reply.message }}</p>
              </article>
            </div>

            <form class="pt-4 border-t border-slate-700/50 space-y-3" @submit.prevent="sendReply">
              <textarea
                v-model="replyText"
                rows="4"
                class="form-input"
                placeholder="Reply to this user…"
                required
              ></textarea>
              <div class="flex justify-end">
                <button type="submit" class="btn-oilbuddy-primary" :disabled="sending">
                  {{ sending ? 'Sending…' : 'Send reply' }}
                </button>
              </div>
            </form>
          </template>
        </section>
      </div>
    </div>

    <div v-if="showCompose" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/60" @click="showCompose = false"></div>
      <form class="relative w-full max-w-lg rounded-3xl border border-slate-700/50 bg-slate-900 p-6 space-y-4" @submit.prevent="sendNewMessage">
        <h3 class="text-xl font-semibold text-white">Message a platform user</h3>
        <div>
          <label class="form-label">User</label>
          <select v-model="compose.userId" class="form-input" required>
            <option value="">Select a user</option>
            <option v-for="user in users" :key="user.id" :value="String(user.id)">
              {{ userLabel(user) }}
            </option>
          </select>
        </div>
        <div>
          <label class="form-label">Subject</label>
          <input v-model="compose.subject" type="text" class="form-input" required>
        </div>
        <div>
          <label class="form-label">Message</label>
          <textarea v-model="compose.message" rows="5" class="form-input" required></textarea>
        </div>
        <div class="flex justify-end gap-3">
          <button type="button" class="btn-secondary" @click="showCompose = false">Cancel</button>
          <button type="submit" class="btn-oilbuddy-primary" :disabled="sending">Send</button>
        </div>
      </form>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import AdminLayout from './AdminLayout.vue'

interface SupportTicket {
  id: number
  subject: string
  message: string
  priority: string
  status: string
  userId?: number
  userName?: string
  userEmail?: string
  userType?: string
  companyName?: string
  createdAt?: string
  updatedAt?: string
}

interface SupportReply {
  id: number
  senderType: string
  senderName?: string
  message: string
  createdAt?: string
}

const tickets = ref<SupportTicket[]>([])
const replies = ref<SupportReply[]>([])
const selected = ref<SupportTicket | null>(null)
const selectedId = ref<number | null>(null)
const users = ref<Array<Record<string, any>>>([])
const search = ref('')
const statusFilter = ref('open')
const replyText = ref('')
const isLoading = ref(false)
const sending = ref(false)
const showCompose = ref(false)
const stats = ref({ open: 0, inProgress: 0, closed: 0, total: 0 })
const compose = reactive({ userId: '', subject: '', message: '' })

const statusFilters = [
  { id: 'open', label: 'Open' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'closed', label: 'Closed' },
  { id: 'all', label: 'All' },
]

const adminHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('adminToken') || ''
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' }
}

const filteredTickets = computed(() => {
  const q = search.value.trim().toLowerCase()
  return tickets.value.filter((ticket) => {
    if (statusFilter.value !== 'all' && ticket.status !== statusFilter.value) return false
    if (!q) return true
    return [ticket.userName, ticket.userEmail, ticket.subject, ticket.companyName, ticket.userType]
      .some((value) => String(value || '').toLowerCase().includes(q))
  })
})

const accountLabel = (ticket: SupportTicket) => {
  const type = String(ticket.userType || 'user').replace('_', ' ')
  return ticket.companyName ? `${type} · ${ticket.companyName}` : type
}

const userLabel = (user: Record<string, any>) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.email
  return `${name} · ${user.email} · ${user.userType || 'user'}`
}

const statusLabel = (status: string) => {
  if (status === 'in_progress') return 'In progress'
  if (status === 'closed') return 'Closed'
  return 'Open'
}

const statusClass = (status: string) => {
  if (status === 'closed') return 'bg-slate-600/40 text-slate-300'
  if (status === 'in_progress') return 'bg-cyan-500/20 text-cyan-300'
  return 'bg-amber-500/20 text-amber-300'
}

const loadTickets = async () => {
  isLoading.value = true
  try {
    const response = await fetch('/api/admin/support', { headers: adminHeaders() })
    const data = await response.json().catch(() => ({}))
    tickets.value = data.tickets || []
    stats.value = data.stats || { open: 0, inProgress: 0, closed: 0, total: tickets.value.length }
    if (selectedId.value && !tickets.value.some((ticket) => ticket.id === selectedId.value)) {
      selected.value = null
      selectedId.value = null
      replies.value = []
    }
  } finally {
    isLoading.value = false
  }
}

const selectTicket = async (id: number) => {
  selectedId.value = id
  const response = await fetch(`/api/admin/support/${id}`, { headers: adminHeaders() })
  const data = await response.json().catch(() => ({}))
  selected.value = data.ticket || tickets.value.find((ticket) => ticket.id === id) || null
  const thread = Array.isArray(data.replies) ? data.replies : []
  replies.value = thread.filter((reply: SupportReply) => reply.message !== selected.value?.message)
}

const sendReply = async () => {
  if (!selected.value || !replyText.value.trim()) return
  sending.value = true
  try {
    const response = await fetch(`/api/admin/support/${selected.value.id}/reply`, {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ message: replyText.value.trim() }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.success === false) throw new Error(data.error || 'Reply failed')
    replyText.value = ''
    await loadTickets()
    await selectTicket(selected.value.id)
  } finally {
    sending.value = false
  }
}

const setStatus = async (status: string) => {
  if (!selected.value) return
  await fetch(`/api/admin/support/${selected.value.id}/status`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify({ status }),
  })
  await loadTickets()
  await selectTicket(selected.value.id)
}

const openCompose = async () => {
  showCompose.value = true
  if (!users.value.length) {
    const response = await fetch('/api/admin/users', { headers: adminHeaders() })
    const data = await response.json().catch(() => ({}))
    users.value = (data.users || []).filter((user: Record<string, any>) => user.userType !== 'admin')
  }
}

const sendNewMessage = async () => {
  sending.value = true
  try {
    const response = await fetch('/api/admin/support/message', {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({
        userId: Number(compose.userId),
        subject: compose.subject,
        message: compose.message,
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.success === false) throw new Error(data.error || 'Could not send message')
    showCompose.value = false
    compose.userId = ''
    compose.subject = ''
    compose.message = ''
    await loadTickets()
    if (data.id) await selectTicket(data.id)
  } finally {
    sending.value = false
  }
}

onMounted(loadTickets)
</script>
